import { set } from 'lodash/fp';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { csrfToken } from 'shared/api_request_helpers';
import AirtableChannel from '../channels/airtable';

const DeleteButton = ({action, className, children}) => (
  <form method="post" action={action}>
    <input type="hidden" name="_method" value="delete" autoComplete="off"/>
    <input type="hidden" name="authenticity_token" value={csrfToken()} autoComplete="off"/>
    <button type="submit" className={className}>{children}</button>
  </form>
);
const SyncButton = ({children, collection, airtableChannel, syncProgress}) => {
  const inProgress = syncProgress[collection] < 1;
  return (
    <button className="btn btn-outline-secondary mr-1"
      onClick={() => airtableChannel.sync(collection)}
      disabled={inProgress}
    >
      <i className={classNames("icon", {"icon-arrows-ccw": !inProgress, "icon-spin4": inProgress, "animate-spin": inProgress})}/>
      {children}
    </button>
  );
};

const AirtableBaseSelection = ({airtableChannel}) => {
  const [bases, setBases] = useState([]);

  useEffect(() => {
    const baseListReceived = ({detail}) => {
      if (detail.bases) setBases(detail.bases);
    };
    airtableChannel.addEventListener("baseList", baseListReceived);
    airtableChannel.getBaseList();
    return () => {
      airtableChannel.removeEventListener("baseList", baseListReceived);
    };
  }, []);

  const selectBase = ({target: {value}}) => {
    const base = _.find(bases, {id: value});
    if (base) airtableChannel.update({base_id: base.id, base_name: base.name});
  };

  return (
    <select className="custom-select" onChange={selectBase}>
      <option>Select a base to connect</option>
      {bases.map(({id, name}) => <option key={id} value={id}>{name}</option>)}
    </select>
  );
};

export default function AirtableSettings({airtableIntegration:ai, authorizer}) {
  const [airtableIntegration, setAirtableIntegration] = useState(ai);
  const [airtableChannel] = useState(() => new AirtableChannel());
  const [syncProgress, setSyncProgress] = useState({listings: 1, inquiries: 1});
  const [syncMessage, setSyncMessage] = useState({listings: "", inquiries: ""});

  const {base_name, base_id, active, error_message} = airtableIntegration;

  useEffect(() => {
    const messageReceived = ({detail}) => {
      const progress = detail.status === 'started' ? ` (${_.round(detail.progress*100)}%)` : '';
      setSyncMessage(set(detail.collection, `Sync ${detail.collection} ${detail.status}${progress}.`));
      setSyncProgress(set(detail.collection, detail.progress));
    };
    airtableChannel.addEventListener("message", messageReceived);
    
    const updateReceived = ({detail}) => setAirtableIntegration(detail.airtable_integration);
    airtableChannel.addEventListener("update", updateReceived);

    airtableChannel.connect();
    return () => {
      airtableChannel.removeEventListener("update", updateReceived);
      airtableChannel.removeEventListener("message", messageReceived);
      airtableChannel.disconnect();
    };
  }, []);

  return (
    <div className="card border border-dark">
      <div className="card-body">
        {base_id ? (
          <h6 className="card-title text-wrap">
            Connected to <a href={`https://airtable.com/${base_id}`} target="_blank"><strong>{base_name}</strong></a>
            <span className="text-nowrap">
              {" "}by
              <div className="avatar avatar-small mx-1" style={{backgroundImage: `url(${authorizer.carrierwave_avatar.url}`}}></div>
              {authorizer.first_name} {authorizer.last_name}
            </span>
          </h6>
        ) : (
          <AirtableBaseSelection airtableChannel={airtableChannel}/>
        )}
        {error_message && (
          <div className="text-danger">{error_message}</div>
        )}
        {_.join(_.values(syncMessage)) && (
          <div className="text-info">{_.join(_.values(syncMessage), ' ')}</div>
        )}
      </div>
        <div className="card-footer">
          <DeleteButton className="btn btn-outline-danger float-right">
            Unlink
          </DeleteButton>

        {base_id && (
          active ? <>
            <SyncButton collection="listings" {...{syncProgress, airtableChannel}}>
              Sync Published Listings
            </SyncButton>
            <SyncButton collection="inquiries" {...{syncProgress, airtableChannel}}>
              Sync Listing Inquiries (Past 30 Days)
            </SyncButton>
          </>
        : (
          <button className="btn btn-warning mr-1"
            onClick={airtableChannel.activate}
          >
            Reactivate
          </button>
        ))}
      </div>
    </div>
  );
}
